import { useRef, useState } from 'react';
import { chevronPoints, linePathD, lineZone } from '@urbanrats/domain';

export interface EditLine {
  a: { x: number; y: number };
  b: { x: number; y: number };
  curve?: { x: number; y: number };
  countedDirection: 'A_TO_B' | 'B_TO_A';
  /** Display-only approach zone: near edge (A′ beside A, B′ beside B); absent = automatic. */
  zone?: { a: { x: number; y: number }; b: { x: number; y: number } };
}

const clamp = (v: number) => Math.min(1, Math.max(0, v));

/**
 * Counting line editor on a camera picture: drag the ends A and B; with "curved" on, drag the middle handle to
 * bend the line (the line passes through it). The yellow approach zone (what players see in front of the line)
 * has two more handles, A′ and B′: place them along the lanes so the zone follows the road's perspective. The
 * zone is display only — only vehicles crossing the drawn line in the chevrons' direction count. The saved
 * line is shown dashed for comparison.
 */
export function LineEditor({ image, ratio, value, saved, onChange, disabled = false }: { image: string; ratio: number; value: EditLine; saved: EditLine | null; onChange: (l: EditLine) => void; disabled?: boolean }) {
  const svg = useRef<SVGSVGElement>(null);
  const [drag, setDrag] = useState<'a' | 'b' | 'curve' | 'za' | 'zb' | 'zm' | null>(null);
  const [dragFrom, setDragFrom] = useState<{ x: number; y: number } | null>(null);
  const W = 1000;
  const H = Math.round(W / ratio);
  const toNorm = (e: React.PointerEvent) => {
    const r = svg.current!.getBoundingClientRect();
    return { x: clamp((e.clientX - r.left) / r.width), y: clamp((e.clientY - r.top) / r.height) };
  };
  const move = (e: React.PointerEvent) => {
    if (!drag || disabled) return;
    const p = toNorm(e);
    if (drag === 'za' || drag === 'zb') {
      const near = value.zone ?? lineZone(value, W, H).near;
      onChange({ ...value, zone: { ...near, [drag === 'za' ? 'a' : 'b']: p } });
    } else if (drag === 'zm') {
      // Whole near edge: A′ and B′ move together (zone depth / position), keeping the perspective.
      const near = value.zone ?? lineZone(value, W, H).near;
      const from = dragFrom ?? p;
      const dx = p.x - from.x;
      const dy = p.y - from.y;
      setDragFrom(p);
      onChange({ ...value, zone: { a: { x: clamp(near.a.x + dx), y: clamp(near.a.y + dy) }, b: { x: clamp(near.b.x + dx), y: clamp(near.b.y + dy) } } });
    } else if (drag === 'curve') onChange({ ...value, curve: p });
    else {
      // Moving an end of a curved line keeps its bend relative to the chord.
      const next = { ...value, [drag]: p };
      if (value.curve) {
        const om = { x: (value.a.x + value.b.x) / 2, y: (value.a.y + value.b.y) / 2 };
        const nm = { x: (next.a.x + next.b.x) / 2, y: (next.a.y + next.b.y) / 2 };
        next.curve = { x: clamp(value.curve.x + nm.x - om.x), y: clamp(value.curve.y + nm.y - om.y) };
      }
      onChange(next);
    }
  };
  const z = lineZone(value, W, H);
  const zonePts = `${z.near.a.x * W},${z.near.a.y * H} ${z.near.b.x * W},${z.near.b.y * H} ${value.b.x * W},${value.b.y * H} ${value.a.x * W},${value.a.y * H}`;
  const chev = (c: (typeof z.chevrons)[number]) =>
    chevronPoints(c, W, H)
      .map((q) => q.join(','))
      .join(' ');
  const nearMid = { x: (z.near.a.x + z.near.b.x) / 2, y: (z.near.a.y + z.near.b.y) / 2 };
  const handle = (k: 'a' | 'b' | 'curve' | 'za' | 'zb' | 'zm', p: { x: number; y: number }, label: string) => (
    <g
      className={`le-handle le-${k} ${drag === k ? 'drag' : ''}`}
      transform={`translate(${p.x * W} ${p.y * H})`}
      onPointerDown={(e) => {
        if (disabled) return;
        (e.target as Element).setPointerCapture?.(e.pointerId);
        setDragFrom(k === 'zm' ? toNorm(e) : null);
        setDrag(k);
      }}
      role="slider"
      aria-label={k === 'curve' ? 'bend point' : k === 'zm' ? 'zone near edge (move A′ and B′ together)' : k === 'za' || k === 'zb' ? `zone corner ${label}` : `end ${label}`}
      aria-valuetext={`${Math.round(p.x * 100)} %, ${Math.round(p.y * 100)} %`}
      tabIndex={0}
      onKeyDown={(e) => {
        const d = e.shiftKey ? 0.01 : 0.002;
        const dx = e.key === 'ArrowLeft' ? -d : e.key === 'ArrowRight' ? d : 0;
        const dy = e.key === 'ArrowUp' ? -d : e.key === 'ArrowDown' ? d : 0;
        if (!dx && !dy) return;
        e.preventDefault();
        const q = { x: clamp(p.x + dx), y: clamp(p.y + dy) };
        if (k === 'zm') {
          const n = value.zone ?? z.near;
          onChange({ ...value, zone: { a: { x: clamp(n.a.x + dx), y: clamp(n.a.y + dy) }, b: { x: clamp(n.b.x + dx), y: clamp(n.b.y + dy) } } });
        } else if (k === 'za' || k === 'zb') onChange({ ...value, zone: { ...(value.zone ?? z.near), [k === 'za' ? 'a' : 'b']: q } });
        else onChange(k === 'curve' ? { ...value, curve: q } : { ...value, [k]: q });
      }}
    >
      {k === 'curve' || k === 'zm' ? <rect x={-11} y={-11} width={22} height={22} rx={4} transform="rotate(45)" /> : k === 'za' || k === 'zb' ? <rect x={-12} y={-12} width={24} height={24} rx={5} /> : <circle r={14} />}
      <text y={5}>{label}</text>
    </g>
  );
  return (
    <div className="line-editor" style={{ aspectRatio: `${ratio}` }}>
      <img src={image} alt="Current camera picture" draggable={false} />
      <svg ref={svg} viewBox={`0 0 ${W} ${H}`} onPointerMove={move} onPointerUp={() => (setDrag(null), setDragFrom(null))} onPointerLeave={() => (setDrag(null), setDragFrom(null))} data-testid="line-editor">
        {saved && <path d={linePathD(saved, W, H)} className="le-saved" />}
        <polygon points={zonePts} className={`le-zone ${z.custom ? 'custom' : ''}`} />
        {z.chevrons.map((c, i) => (
          <polyline key={i} points={chev(c)} className="le-chev" />
        ))}
        <path d={linePathD(value, W, H)} className="le-casing" />
        <path d={linePathD(value, W, H)} className="le-line" />
        {value.curve && <line x1={(value.a.x + value.b.x) * W * 0.5} y1={(value.a.y + value.b.y) * H * 0.5} x2={value.curve.x * W} y2={value.curve.y * H} className="le-guide" />}
        {handle('a', value.a, 'A')}
        {handle('b', value.b, 'B')}
        {value.curve && handle('curve', value.curve, '')}
        {handle('za', z.near.a, 'A′')}
        {handle('zb', z.near.b, 'B′')}
        {handle('zm', nearMid, '')}
      </svg>
    </div>
  );
}
